/**
 * BecomeCoachPage - Page for users to create an optional coach profile
 * V2: Coach profiles are optional - anyone can be invited as a coach
 * This page allows users to fill in profile info that will be displayed
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Dumbbell,
  User,
  FileText,
  Check,
  Loader2,
  Info,
} from 'lucide-react';
import { useCoachStore, COACH_SPECIALIZATIONS } from '../../stores/coachStore';

const BecomeCoachPage = () => {
  const navigate = useNavigate();
  const {
    createCoachProfile,
    updateCoachProfile,
    hasCoachProfile,
    coachProfileData,
    isActingAsCoach,
    isLoadingCoachProfile,
  } = useCoachStore();

  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    specializations: [],
    isAcceptingClients: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill form if profile exists
  useEffect(() => {
    if (hasCoachProfile && coachProfileData) {
      setFormData({
        displayName: coachProfileData.display_name || '',
        bio: coachProfileData.bio || '',
        specializations: coachProfileData.specializations || [],
        isAcceptingClients: coachProfileData.is_accepting_clients ?? true,
      });
    }
  }, [hasCoachProfile, coachProfileData]);

  const handleSpecializationToggle = (spec) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter((s) => s !== spec)
        : [...prev.specializations, spec],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.displayName.trim()) {
      setError('Lütfen görünen isminizi girin');
      return;
    }

    if (formData.specializations.length === 0) {
      setError('En az bir uzmanlık alanı seçin');
      return;
    }

    setIsSubmitting(true);
    try {
      const profileData = {
        displayName: formData.displayName.trim(),
        coachType: 'fitness',
        bio: formData.bio.trim() || null,
        specializations: formData.specializations,
        isAcceptingClients: formData.isAcceptingClients,
      };

      if (hasCoachProfile) {
        await updateCoachProfile(profileData);
      } else {
        await createCoachProfile(profileData);
      }

      // Navigate to coach dashboard if acting as coach, otherwise go back
      if (isActingAsCoach) {
        navigate('/coach');
      } else {
        navigate(-1);
      }
    } catch (err) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingCoachProfile) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Dumbbell className="w-6 h-6 text-orange-500" />
                {hasCoachProfile ? 'Koç Profilini Düzenle' : 'Koç Profili Oluştur'}
              </h1>
              <p className="text-sm text-gray-400">
                {hasCoachProfile
                  ? 'Koç profil bilgilerinizi güncelleyin'
                  : 'Koç profilinizi oluşturarak kendinizi tanıtın'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      {!hasCoachProfile && (
        <div className="max-w-3xl mx-auto px-4 pt-6">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-medium mb-1">Koç profili opsiyoneldir</p>
              <p className="text-blue-300/80">
                Herkes sizi koç olarak davet edebilir. Bu profili oluşturarak,
                öğrencileriniz sizin hakkınızda daha fazla bilgi görebilir.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Display Name */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-semibold">Görünen İsim</h2>
                <p className="text-sm text-gray-400">
                  Öğrencilerinizin göreceği isim
                </p>
              </div>
            </div>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, displayName: e.target.value }))
              }
              placeholder="Örn: Ahmet Yılmaz"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
              maxLength={50}
            />
          </div>

          {/* Specializations */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="font-semibold">Uzmanlık Alanları</h2>
                <p className="text-sm text-gray-400">
                  Hangi alanlarda koçluk yapıyorsunuz?
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(COACH_SPECIALIZATIONS).map(([key, spec]) => (
                <motion.button
                  key={key}
                  type="button"
                  onClick={() => handleSpecializationToggle(key)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    formData.specializations.includes(key)
                      ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                      : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{spec.emoji}</span>
                    <span className="text-sm font-medium">{spec.label}</span>
                    {formData.specializations.includes(key) && (
                      <Check className="w-4 h-4 ml-auto" />
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Bio */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="font-semibold">Hakkınızda</h2>
                <p className="text-sm text-gray-400">
                  Kendinizi tanıtın (isteğe bağlı)
                </p>
              </div>
            </div>
            <textarea
              value={formData.bio}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, bio: e.target.value }))
              }
              placeholder="Deneyimleriniz, sertifikalarınız, koçluk yaklaşımınız..."
              rows={4}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors resize-none"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-2 text-right">
              {formData.bio.length}/500
            </p>
          </div>

          {/* Accepting Clients Toggle */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <h2 className="font-semibold">Yeni Öğrenci Kabul Et</h2>
                <p className="text-sm text-gray-400">
                  Şu an yeni öğrenci davetlerini kabul edebilir misiniz?
                </p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.isAcceptingClients}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isAcceptingClients: e.target.checked,
                    }))
                  }
                  className="sr-only"
                />
                <div
                  className={`w-14 h-8 rounded-full transition-colors ${
                    formData.isAcceptingClients ? 'bg-orange-500' : 'bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform mt-1 ${
                      formData.isAcceptingClients ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {hasCoachProfile ? 'Güncelleniyor...' : 'Oluşturuluyor...'}
              </>
            ) : (
              <>
                <Dumbbell className="w-5 h-5" />
                {hasCoachProfile ? 'Profili Güncelle' : 'Koç Profilimi Oluştur'}
              </>
            )}
          </motion.button>
        </form>
      </main>
    </div>
  );
};

export default BecomeCoachPage;
